import React from 'react';
import {
  InnerPageHeader, Card, SegmentedControl, ErrorState, EmptyState, Button, phone, } from '@parsina/framework';
import { UserX } from 'lucide-react';
import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { usePatient } from '../../hooks/usePatient.js';
import { genderKey } from '../../lib/patients.js';
import SummaryTab from './tabs/SummaryTab.jsx';
import HistoryTab from './tabs/HistoryTab.jsx';
import ChartTab from './tabs/ChartTab.jsx';
import PlanTab from './tabs/PlanTab.jsx';
import { PATIENT_SECTIONS } from './PatientDetail.shared.js';
import PatientAvatar from '../../components/PatientAvatar.jsx';
import { PatientDetailSkeletonWeb } from './PatientDetail.skeleton.jsx';

// The patient workspace.
//
// THREE TABS: خلاصه (who this patient is, and what they are allergic to),
// چارت دندان (the odontogram) and سوابق (what has been recorded).
//
// چارت دندان arrived once the tooth key was frozen — FDI/ISO 3950, decided
// 2026-08-02, clearing the phase-0 gate (CORE.md §6). Drawing a chart commits
// to a tooth model, which is exactly why it could not exist before that
// decision was made deliberately.
//
// Still absent, still behind their own decisions: طرح درمان (a priced,
// patient-signed commitment), پریو, and لابراتوار.

// The section set is PatientDetail.shared.js — the mobile strip and the route
// whitelist read the SAME list, which is what stops a tab existing that no URL
// can reach.
const TABS = PATIENT_SECTIONS;

const PatientDetailWeb = ({ id, tab, setTab, goBack }) => {
  const { t, formatNumber, formatDate } = useTranslation();
  const navigate = useAppNavigate();
  const { activeId } = useClinicScope();
  const { patient, loading, error, reload } = usePatient(id, activeId);

  // The record's OWN shape while it loads — header band, tab strip and the
  // body of the tab the URL names — so a reload on /patients/:id/chart draws
  // the chart's shape and nothing shifts when the data lands (DESIGN.md
  // §"Loading & skeleton").
  if (loading && !patient) {
    return <PatientDetailSkeletonWeb tab={tab} label={t('common.loading')} />;
  }

  // A failed read and a record that does not exist are different facts: only one
  // of them is worth a retry button.
  if (error && !patient) {
    return (
      <div
        data-testid="patient-detail-error"
        data-state="error"
        role="alert"
        style={{ height: '100%', background: C.bg, padding: 32 }}
      >
        <Card>
          <ErrorState
            title={t('patient.error.title')}
            description={t('errors.load.patient')}
            onRetry={reload}
            retryLabel={t('common.actions.retry')}
          />
        </Card>
      </div>
    );
  }

  if (!patient) {
    return (
      <div style={{ height: '100%', background: C.bg, padding: 32 }}>
        <Card>
          <EmptyState
            icon={<UserX size={48} color={C.textMuted} />}
            title={t('patient.notFound.title')}
            description={t('patient.notFound.description')}
            action={<Button variant="outline" onClick={goBack}>{t('common.actions.back')}</Button>}
          />
        </Card>
      </div>
    );
  }

  const gKey = genderKey(patient.gender);
  const meta = [
    gKey ? t(gKey) : patient.gender || null,
    patient.phone ? (
      <span key="phone" dir="ltr" style={{ unicodeBidi: 'isolate' }}>{phone.forDisplay(patient.phone)}</span>
    ) : null,
    patient.fileNumber ? `${t('common.labels.fileNumber')}${t('common.punct.colon')} ${patient.fileNumber}` : null,
  ].filter(Boolean);

  const stats = [
    patient.age != null ? { label: t('patient.stats.age'), value: formatNumber(patient.age) } : null,
    {
      label: t('patient.stats.allergies'),
      value: formatNumber((patient.allergies || []).length),
      accent: (patient.allergies || []).length > 0,
    },
    { label: t('patient.stats.medications'), value: formatNumber((patient.medications || []).length) },
  ].filter(Boolean);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}>
      <InnerPageHeader
        onBack={goBack}
        backLabel={t('common.actions.back')}
        title={patient.name}
        avatar={<PatientAvatar name={patient.name} src={patient.avatarUrl} size={44} bg={C.navy} />}
        meta={meta}
        stats={stats}
      >
        <Button onClick={() => navigate('prescription', { patientId: patient.id })}>
          {t('patient.actions.prescribe')}
        </Button>
      </InnerPageHeader>

      <div style={{ padding: '14px 28px 0', flexShrink: 0 }}>
        <SegmentedControl options={TABS(t)} value={tab} onChange={setTab} />
      </div>

      {/* The kit's SegmentedControl declares role="tablist"/role="tab"; this is
          the other half of the pattern. Without it a screen reader announces
          «سوابق tab, selected» over a region it has no relationship to.
          `aria-label` rather than `aria-labelledby` — the kit generates no ids. */}
      <div
        role="tabpanel"
        aria-label={TABS(t).find((s) => s.value === tab)?.label}
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 28px 40px' }}
      >
        {tab === 'chart'
          ? <ChartTab patientId={id} clinicId={activeId} t={t} formatDate={formatDate} />
          : tab === 'plan'
            ? <PlanTab patientId={id} clinicId={activeId} t={t} formatNumber={formatNumber} formatDate={formatDate} />
            : tab === 'history'
              ? <HistoryTab patient={patient} />
              : <SummaryTab patient={patient} />}
      </div>
    </div>
  );
};

export default PatientDetailWeb;
